import React, { useMemo, useRef, useState, useEffect } from "react";
import type { FeedItem, PostMedia } from "../../types";
import styles from "./Post.module.scss";
import { MessageCircle, Share2, ThumbsUp, MoreVertical, Pencil, Trash2 } from "lucide-react";
import MediaCarousel from "../MediaCarousel/MediaCarousel";
import Avatar from "../Avatar/Avatar";
import FollowButton from "../FollowButton/FollowButton";
import { useUser } from "../../context/UserContext";
import { useAlert } from "../../hooks/useAlert";
import LikesModal from "../LikesModal/LikesModal";

interface PostProps {
  item: FeedItem;
  onLikeToggle?: (postId: string, nextLiked: boolean) => void;
  onOpenComments?: (postId: string) => void;
  onDelete?: (postId: string) => void;
  onEdit?: (postId: string, newBody: string) => void;
  onAuthorClick?: (userId: string) => void;
  showFollowButton?: boolean;
  commentsExpanded?: boolean;
}

function pickPrimaryMedia(media?: PostMedia[]) {
  if (!media?.length) return null;
  return (
    media.find((m) => m.type === "video") ??
    media.find((m) => m.type === "image") ??
    media[0]
  );
}

function getAspectRatio(primary: PostMedia | null) {
  if (primary?.aspectRatio && Number.isFinite(primary.aspectRatio) && primary.aspectRatio > 0) {
    return `${primary.aspectRatio} / 1`;
  }
  if (primary?.width && primary?.height && primary.width > 0 && primary.height > 0) {
    return `${primary.width} / ${primary.height}`;
  }
  return "4 / 5";
}

const Post: React.FC<PostProps> = ({ item, onLikeToggle, onOpenComments, onDelete, onEdit, onAuthorClick, showFollowButton = false, commentsExpanded = false }) => {
  const { post, author, likedByMe } = item;
  const { user: currentUser } = useUser();
  const { confirm } = useAlert();
  const [isFollowing, setIsFollowing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editBody, setEditBody] = useState(post.body);
  const [likesModalOpen, setLikesModalOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const likeInFlight = useRef<boolean>(false);

  // Reset in-flight lock when likedByMe changes (optimistic update applied or reverted)
  useEffect(() => {
    likeInFlight.current = false;
  }, [likedByMe]);

  const isOwnPost = currentUser?.userId === author.userId;

  // Close menu when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  const createdLabel = useMemo(() => {
    const d = new Date(post.createdAt);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);

    if (diffSec < 60) return "Just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;

    return d.toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }, [post.createdAt]);

  const media = useMemo(
    () => (post.media ?? []).filter((m) => m.type === "image" || m.type === "video"),
    [post.media]
  );

  const hasMediaBg = media.length > 0;
  const primaryMedia = useMemo(() => pickPrimaryMedia(media), [media]);
  const mediaAspect = useMemo(
    () => (hasMediaBg ? getAspectRatio(primaryMedia) : undefined),
    [hasMediaBg, primaryMedia]
  );

  const handleEditSubmit = () => {
    const trimmed = editBody.trim();
    if (!trimmed || trimmed === post.body) {
      setIsEditing(false);
      setEditBody(post.body);
      return;
    }
    onEdit?.(post._id, trimmed);
    setIsEditing(false);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditBody(post.body);
  };

  return (
    <article
      className={`${styles.post} ${hasMediaBg ? styles.hasMedia : ""} ${commentsExpanded ? styles.commentsOpen : ""}`}
      style={hasMediaBg ? ({ aspectRatio: mediaAspect } as React.CSSProperties) : undefined}
    >
      {hasMediaBg && (
        <>
          <div className={styles.bg}>
            <MediaCarousel media={media} />
            <div className={styles.scrim} />
            <div className={styles.vignette} />
          </div>
          <div className={styles.swipeZone} aria-hidden="true" />
        </>
      )}

      <div className={styles.content}>
        <header className={styles.postHeader}>
          <Avatar
            src={author.profileImageUrl || author.avatarUrl}
            username={author.username}
            size="md"
            className={styles.avatar}
            onClick={() => onAuthorClick?.(author.userId)}
          />

          <div className={styles.meta}>
            <button type="button" className={styles.username} onClick={() => onAuthorClick?.(author.userId)}>
              {author.username}
            </button>
            <time className={styles.timestamp}>{createdLabel}</time>
          </div>

          {showFollowButton && !isOwnPost && (
            <div className={styles.followButtonWrapper}>
              <FollowButton
                targetUserId={author.userId}
                initialIsFollowing={isFollowing}
                onFollowChange={setIsFollowing}
              />
            </div>
          )}

          {/* 3-dot menu for post owner */}
          {isOwnPost && (
            <div className={styles.postMenu} ref={menuRef}>
              <button
                type="button"
                className={styles.menuTrigger}
                onClick={() => setMenuOpen((prev) => !prev)}
                aria-label="Post options"
              >
                <MoreVertical size={18} />
              </button>

              {menuOpen && (
                <div className={styles.menuDropdown}>
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      setIsEditing(true);
                      setEditBody(post.body);
                    }}
                  >
                    <Pencil size={14} /> Edit
                  </button>
                  <button
                    type="button"
                    className={styles.menuDanger}
                    onClick={async () => {
                      setMenuOpen(false);
                      const confirmed = await confirm({
                        message: "Are you sure you want to delete this post? This cannot be undone.",
                        type: "warning",
                        confirmText: "Delete",
                        cancelText: "Cancel",
                      });
                      if (confirmed) {
                        onDelete?.(post._id);
                      }
                    }}
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              )}
            </div>
          )}
        </header>

        <div className={styles.bottom}>
          {isEditing ? (
            <div className={styles.editContainer}>
              <textarea
                className={styles.editTextarea}
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
                autoFocus
                maxLength={2000}
              />
              <div className={styles.editActions}>
                <button type="button" className={styles.editCancel} onClick={handleEditCancel}>
                  Cancel
                </button>
                <button
                  type="button"
                  className={styles.editSave}
                  onClick={handleEditSubmit}
                  disabled={!editBody.trim() || editBody.trim() === post.body}
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            post.body?.trim() && <p className={styles.postBody}>{post.body}</p>
          )}

          <footer className={styles.postActions}>
            <button
              type="button"
              aria-pressed={likedByMe}
              onClick={() => {
                if (likeInFlight.current) return;
                likeInFlight.current = true;
                onLikeToggle?.(post._id, !likedByMe);
              }}
            >
              <ThumbsUp size={18} /> {post.likeCount}
            </button>

            <button type="button" onClick={() => onOpenComments?.(post._id)}>
              <MessageCircle size={18} /> {post.commentCount}
            </button>

            <button type="button" onClick={() => onOpenComments?.(post._id)}>
              <Share2 size={18} />
            </button>
          </footer>

          {post.likeCount > 0 && item.recentLiker && (
            <div className={styles.likedByRow}>
              <span>
                Liked by <strong className={styles.likedByUser} onClick={() => onAuthorClick?.(item.recentLiker!.userId)}>{item.recentLiker.username}</strong>
                {post.likeCount > 1 && (
                  <> and <button type="button" className={styles.likedByOthers} onClick={() => setLikesModalOpen(true)}>others</button></>
                )}
              </span>
            </div>
          )}

          <LikesModal postId={post._id} open={likesModalOpen} onClose={() => setLikesModalOpen(false)} />
        </div>
      </div>
    </article>
  );
};

export default Post;
